import { INSTRUMENT_DEFINITIONS } from "@ferrivia/api-contract";
import type { EventProperties } from "@ferrivia/analytics/browser";
import { isAnswered, type AnswerMap } from "./answerMap";
import type { ArticleRule } from "./articles";

type DisclosureId = EventProperties<"summary.disclosure_toggled">["section_id"];

export type LabelOverride = "none" | "additionalInformation";

export type Entry =
  | { type: "answer"; key: string; label?: LabelOverride }
  | { type: "gated"; gate: string; followUps: readonly string[] };

export interface Section {
  id:
    | "lifeStage"
    | "conditions"
    | "menstruation"
    | "pregnancy"
    | "bloodDonation"
    | "diet"
    | "exercise"
    | "gut"
    | "other";
  lead: readonly Entry[];
  article?: ArticleRule;
  folded?: { disclosure: DisclosureId; entries: readonly Entry[]; foldedLabel: "all" | "others" };
}

const answer = (key: string, label?: LabelOverride): Entry => ({ type: "answer", key, label });
const gated = (gate: string, ...followUps: string[]): Entry => ({ type: "gated", gate, followUps });

export const SECTIONS: readonly Section[] = [
  {
    id: "lifeStage",
    lead: [answer("female_reproductive_stage", "none")],
    article: { rule: "life_stage" },
  },
  {
    id: "conditions",
    lead: [],
    article: { rule: "conditions" },
    folded: {
      disclosure: "conditions",
      foldedLabel: "all",
      entries: [
        gated("medical_conditions", "medical_conditions_details", "medical_conditions_other"),
        gated("medication", "medication_details"),
      ],
    },
  },
  {
    id: "menstruation",
    lead: [],
    article: { rule: "fixed", slot: "menstruation" },
    folded: {
      disclosure: "menstruation",
      foldedLabel: "all",
      entries: [
        answer("menstruation_regularity"),
        answer("menstrual_cycle_consistency"),
        answer("menstrual_cycle_length"),
        answer("menstrual_cycle_shortest"),
        answer("menstrual_cycle_longest"),
        answer("menstruation_length"),
        answer("menstruation_amount"),
        answer("heavy_menstruation_signs"),
      ],
    },
  },
  {
    id: "pregnancy",
    lead: [],
    article: { rule: "pregnancy" },
    folded: {
      disclosure: "pregnancy_contraception",
      foldedLabel: "all",
      entries: [
        gated("contraceptives", "contraceptives_details", "contraceptives_other"),
        gated("child_birth"),
        answer("no_of_children"),
        answer("last_childbirth"),
        gated("miscarriage"),
        answer("no_of_miscarriages"),
        answer("last_miscarriage"),
      ],
    },
  },
  {
    id: "bloodDonation",
    lead: [gated("blood_donation", "blood_donation_details")],
    article: { rule: "blood_donation" },
  },
  {
    id: "diet",
    lead: [answer("diet")],
    article: { rule: "fixed", slot: "diet" },
    folded: {
      disclosure: "diet",
      foldedLabel: "others",
      entries: [answer("dairy"), answer("coffee_tea"), answer("allergies")],
    },
  },
  {
    id: "exercise",
    lead: [answer("exercise")],
    article: { rule: "fixed", slot: "exercise" },
  },
  {
    id: "gut",
    lead: [],
    article: { rule: "fixed", slot: "gut" },
    folded: {
      disclosure: "gut",
      foldedLabel: "all",
      entries: [
        answer("bowel_movement"),
        answer("stool_quality"),
        answer("gut_symptoms"),
        gated("avoiding_foods", "avoided_foods"),
        gated("diagnosed_gut_conditions", "gut_conditions_details"),
      ],
    },
  },
  {
    id: "other",
    lead: [answer("other_additional_information", "additionalInformation")],
  },
];

export const NOT_SHOWN_KEYS: readonly string[] = ["sleep", "smoking", "alcohol_consumption"];

export interface SupplementGroup {
  id: "iron_supplements" | "other_supplements";
  gate: string;
  groupKey: string;
  split: "still_using" | null;
  behindRow: readonly string[];
  subtitleFields: readonly string[];
}

export const SUPPLEMENT_GROUPS: readonly SupplementGroup[] = [
  {
    id: "iron_supplements",
    gate: "currently_on_iron_supplement",
    groupKey: "iron_supplements",
    split: "still_using",
    behindRow: [
      "daily_iron_dose",
      "frequency",
      "timing",
      "with_meals",
      "stop_reason",
      "side_effects",
      "side_effects_detail",
    ],
    subtitleFields: ["started_at", "stopped_at", "still_using"],
  },
  {
    id: "other_supplements",
    gate: "other_supplements",
    groupKey: "other_supplement_items",
    split: null,
    behindRow: ["nutrients"],
    subtitleFields: [],
  },
];

export const INFUSION = {
  gate: "iron_infusion",
  groupKey: "iron_infusion_items",
  details: "iron_infusion_details",
  behindRow: ["place", "dose_mg"],
} as const;

export const SUPPLEMENTS_ARTICLE: ArticleRule = { rule: "fixed", slot: "supplements" };

export function layoutKeys(): string[] {
  const keys = new Set<string>(NOT_SHOWN_KEYS);
  const addEntry = (entry: Entry) => {
    if (entry.type === "answer") {
      keys.add(entry.key);
    } else {
      keys.add(entry.gate);
      entry.followUps.forEach((key) => keys.add(key));
    }
  };
  for (const section of SECTIONS) {
    section.lead.forEach(addEntry);
    section.folded?.entries.forEach(addEntry);
  }
  for (const group of SUPPLEMENT_GROUPS) {
    keys.add(group.gate);
    keys.add(group.groupKey);
  }
  keys.add(INFUSION.gate);
  keys.add(INFUSION.groupKey);
  keys.add(INFUSION.details);
  return [...keys];
}

export function followUpsAreText(entry: Extract<Entry, { type: "gated" }>): boolean {
  const questions = INSTRUMENT_DEFINITIONS.health_questionnaire.questions;
  return (
    entry.followUps.length > 0 &&
    entry.followUps.every((key) => {
      const question = questions.find((candidate) => candidate.key === key);
      return question !== undefined && !question.repeating && question.kind === "text";
    })
  );
}

export function entryHasContent(entry: Entry, answers: AnswerMap): boolean {
  if (entry.type === "answer") {
    return isAnswered(answers[entry.key]);
  }
  const gate = answers[entry.gate];
  if (gate?.kind !== "boolean") {
    return false;
  }
  if (!gate.value) {
    return true;
  }
  if (followUpsAreText(entry)) {
    return true;
  }
  return entry.followUps.some((key) => isAnswered(answers[key]));
}

export function sectionHasContent(section: Section, answers: AnswerMap): boolean {
  return [...section.lead, ...(section.folded?.entries ?? [])].some((entry) => entryHasContent(entry, answers));
}
